/**
 * The categories of a codebook variable: pure, so the MCP server can use it
 * too (mcp-server/src/web.ts). `utils/codebookUtils` re-exports it.
 */

import type { CodebookData } from "types/codebook.types"
import type { HierarchicalDataEntry } from "types/engineOutput.types"

/**
 * Returns the labels of a container's direct BOOL children — the "categories"
 * a CAT step maps into. Walks the codebook (an array of HierarchicalDataEntry)
 * down the `//`-separated container path, then collects each child whose type
 * is BOOL. Returns `[]` when the container isn't found or has no BOOL children
 * (e.g. it hasn't been applied to the engine session yet).
 */
export const getBoolChildLabels = (
  codebook: CodebookData | null | undefined,
  containerPath: string | null | undefined
): string[] => {
  if (!codebook || !containerPath || !Array.isArray(codebook)) return []
  const parts = containerPath
    .split("//")
    .map((p) => p.trim())
    .filter(Boolean)
  let current = codebook as HierarchicalDataEntry[]
  for (const part of parts) {
    const node = current.find((e) => e && e.label === part)
    if (!node || node.type !== "TABLE" || !Array.isArray(node.value)) {
      return []
    }
    current = node.value as HierarchicalDataEntry[]
  }
  return current
    .filter(
      (e): e is HierarchicalDataEntry =>
        !!e && e.type === "BOOL" && typeof e.label === "string"
    )
    .map((e) => e.label)
}
